/**
 * Connect a system — and, when it needs setting up first, say exactly what to do.
 *
 * ── The half a "Connect X" button usually leaves out ────────────────────────────────────────
 *
 * Pressing Connect only works once *someone has created an app in their own system*. Products that
 * skip that step show a button which fails with an opaque provider error, and the person cannot tell
 * whether the problem is theirs or ours. So this screen shows, per provider:
 *
 *   • whether it can be connected **right now** — which needs both a complete connector *and* our
 *     own credentials to exist, and those are different facts;
 *   • the numbered steps to do in their own console, with the **exact callback URL and scope list**
 *     to copy, both derived from the descriptor so neither can drift from what the code requests;
 *   • what is missing on our side, named — never "not configured".
 *
 * ⚠️ **A client secret cannot be pasted here, and the screen says so.** It lives in the Worker's
 * secret store; a field for it on this screen would be a field that puts it in a browser.
 */
import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Callout, Card, EmptyState, HelpText, Skeleton } from '@dedupr/ui';
import { accessToken } from '../auth.js';
import { InstallOffer } from '../InstallOffer.js';
import './Connect.css';

interface SetupStepView {
  title: string;
  detail: string;
  copyValue?: string;
  link?: string;
}

interface ProviderView {
  providerId: string;
  providerName: string;
  transport: 'rest' | 'file';
  can_connect_now: boolean;
  serverReady: boolean;
  serverMissing: string[];
  callback_url: string | null;
  steps: SetupStepView[];
  /** Whether this provider's descriptor can enumerate custom objects (FR-4.8). */
  supports_custom_objects?: boolean;
}

interface ConnectionView {
  id: string;
  provider: string;
  connectedAt: number;
  needsReconnect: boolean;
}

/** Copy-to-clipboard that reports what happened, because a silent copy is indistinguishable from none. */
function CopyValue({ value, label }: { value: string; label: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  return (
    <div className="web-connect__copy">
      <code className="web-connect__copyvalue">{value}</code>
      <Button
        variant="secondary"
        size="sm"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setState('copied');
          } catch {
            /* Clipboard access is refused outside a secure context and in some embedded browsers.
               Saying so beats a button that silently does nothing — the value is on screen and can be
               selected by hand. */
            setState('failed');
          }
        }}
        aria-label={`Copy ${label}`}
      >
        {state === 'copied' ? 'Copied' : state === 'failed' ? 'Select it manually' : 'Copy'}
      </Button>
    </div>
  );
}

function ProviderCard({
  provider,
  connections,
  onConnect,
}: {
  provider: ProviderView;
  connections: ConnectionView[];
  /** Starts the OAuth flow. Passed in so the card holds no fetch of its own. */
  onConnect: (providerId: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const mine = connections.filter((c) => c.provider === provider.providerId);

  return (
    <Card
      title={provider.providerName}
      headingLevel={3}
      action={
        provider.transport === 'file' ? (
          <Button onClick={() => { window.location.hash = '#/local'; }}>Upload a file</Button>
        ) : provider.can_connect_now ? (
          /* ⚠️ Was `window.location.href = '/api/relay/connect/…'`, a top-level navigation. That
             cannot carry an `Authorization` header, and since B-133 the route requires one — so the
             flow is now: POST with the token, receive the provider's authorize URL, then navigate.
             The consent screen still happens in the top-level window, which is the part that
             genuinely has to. */
          <Button onClick={() => void onConnect(provider.providerId)}>
            Connect {provider.providerName}
          </Button>
        ) : (
          <Button variant="secondary" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? 'Hide setup steps' : 'What is needed'}
          </Button>
        )
      }
    >
      <div className="web-connect__status">
        {mine.length > 0 ? (
          <Badge variant={mine.some((c) => c.needsReconnect) ? 'warning' : 'success'}>
            {mine.some((c) => c.needsReconnect) ? 'Reconnect needed' : `Connected (${mine.length})`}
          </Badge>
        ) : provider.transport === 'file' ? (
          <Badge variant="neutral">No setup needed</Badge>
        ) : provider.can_connect_now ? (
          <Badge variant="neutral">Ready to connect</Badge>
        ) : (
          /* Not "unavailable". The reason is known, and hiding it makes a solvable setup step look
             like a missing feature. */
          <Badge variant="warning">Needs setup first</Badge>
        )}
      </div>

      {provider.transport === 'file' ? (
        <HelpText>
          Read in this browser and never sent anywhere — so there is no app to create and no access to
          grant. This is the fastest way to see what we find.
        </HelpText>
      ) : null}

      {/* Custom-object mapping (FR-4.8, G2-7): only where a connection exists AND the descriptor can
          enumerate custom objects — an always-rendered link that usually 501s would teach people the
          feature is broken. One button per connection, because two Salesforce orgs have different
          objects and a link that silently picked one would map the wrong system's data. */}
      {provider.supports_custom_objects && mine.length > 0 ? (
        <div className="web-connect__custom">
          {mine.map((conn) => (
            <Button
              key={conn.id}
              variant="secondary"
              size="sm"
              onClick={() => {
                window.location.hash = `#/sources/custom-objects?connection=${encodeURIComponent(conn.id)}`;
              }}
            >
              {mine.length === 1
                ? 'Custom objects'
                : `Custom objects (connected ${new Date(conn.connectedAt).toLocaleDateString()})`}
            </Button>
          ))}
        </div>
      ) : null}

      {provider.serverMissing.length > 0 ? (
        <Callout tone="info" title="One step is ours, not yours">
          This deployment is missing {provider.serverMissing.join(' and ')}. Once you have created the
          app below, send us the client id and secret and we will set them — the secret is stored as a
          Worker secret and never reaches a browser, so it cannot be pasted into this page.
        </Callout>
      ) : null}

      {(open || provider.can_connect_now === false) && provider.transport === 'rest' ? (
        <ol className="web-connect__steps">
          {provider.steps.map((step, i) => (
            <li key={i} className="web-connect__step">
              <p className="web-connect__steptitle">
                {step.link ? (
                  // `noreferrer` as well as `noopener`: this points at a third-party console, and the
                  // referrer would disclose the tenant's own hostname.
                  <a href={step.link} target="_blank" rel="noopener noreferrer">
                    {step.title}
                  </a>
                ) : (
                  step.title
                )}
              </p>
              <p className="web-connect__stepdetail">{step.detail}</p>
              {step.copyValue ? <CopyValue value={step.copyValue} label={step.title} /> : null}
            </li>
          ))}
        </ol>
      ) : null}
    </Card>
  );
}

export function Connect({ orgId }: { orgId?: string }) {
  const [providers, setProviders] = useState<ProviderView[] | null>(null);
  const [connections, setConnections] = useState<ConnectionView[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      /* ⚠️ The connections list is authenticated now (B-133), so it needs the access token — held in
         memory only, never in storage (ADR-069), and fetched fresh so a stale one is refreshed rather
         than sent and refused. `/setup` stays unauthenticated on purpose: it returns the provider
         descriptors and this deployment's callback URLs, which are not secrets and are the same for
         everyone. */
      const token = await accessToken();
      const authed: HeadersInit | undefined = token ? { authorization: `Bearer ${token}` } : undefined;
      const [setup, conns] = await Promise.all([
        fetch('/api/relay/setup').then((r) => r.json() as Promise<{ providers: ProviderView[] }>),
        fetch('/api/relay/connections', { headers: authed }).then(
          (r) => r.json() as Promise<{ connections: ConnectionView[] }>
        ),
      ]);
      setProviders(setup.providers);
      setConnections(conns.connections ?? []);
      setError(null);
    } catch {
      /* Under `vite dev` there is no Worker, so these routes return the SPA shell and the JSON parse
         fails. Said plainly rather than shown as a broken screen — it is a development-mode fact, not
         a defect the reader can fix. */
      setError(
        'The connector list could not be read. If this is a local dev server without the Worker running, that is expected — the routes live in the Worker.'
      );
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Start the OAuth flow: ask our own Worker for the provider's authorize URL, then navigate there.
   *
   * Two steps rather than one navigation because the route is authenticated (B-133) and a top-level
   * navigation sends no `Authorization` header. A refusal surfaces on this screen — the Worker's own
   * message, which names whether the problem is a missing session, no organisation, or an
   * unconfigured client secret — rather than as a blank provider error page.
   */
  const startConnect = useCallback(async (providerId: string) => {
    setError(null);
    try {
      const token = await accessToken();
      /* ⚠️ **Names the organisation** ([B-143](../../../../docs/platform/TODO.md)). The Worker needs
         to know which client a source belongs to *before* the redirect, because the callback comes
         back as a top-level navigation with no session to ask. A member of exactly one org needs no
         parameter, but a member of two got a 409 `org_required` with no way to answer it — the
         route supported `?org=` from the start and nothing ever sent it.
         The answer is the org switcher's current selection, deliberately, rather than a second
         picker inside this panel: the app already has one control for "which client am I working
         on", and a source connected under a different org from the one on screen is a mistake
         nobody would notice until the records showed up in the wrong place. */
      const query = orgId ? `?org=${encodeURIComponent(orgId)}` : '';
      const response = await fetch(`/api/relay/connect/${providerId}${query}`, {
        method: 'POST',
        headers: token ? { authorization: `Bearer ${token}` } : undefined,
      });
      const body = (await response.json()) as { authorize_url?: string; detail?: string };
      if (!response.ok || !body.authorize_url) {
        setError(body.detail ?? `Could not start the connection (${response.status}).`);
        return;
      }
      // The consent screen must be top-level: a provider's authorize page refuses to be framed, and
      // a popup is blocked as often as not.
      window.location.href = body.authorize_url;
    } catch {
      setError('Could not reach the connector service. If this is a dev server without the Worker, that is expected.');
    }
    /* ⚠️ `orgId`, not `[]`. This callback now reads it, and an empty dependency list would capture
       whichever org happened to be selected when the panel first mounted — so switching client and
       then connecting would attach the source to the *previous* one. Nothing on screen would say
       so; the records would simply arrive under the wrong client. */
  }, [orgId]);

  /* The callback redirects back here with an outcome in the hash. Read once and cleared, so a reload
     does not re-announce a connection that happened minutes ago. */
  const [outcome, setOutcome] = useState<{
    connected?: string;
    error?: string;
    detail?: string;
    /** Read scopes the consent screen withheld — a Degraded connection, not a failed one (US-1.3). */
    readScopeWithheld?: string;
  } | null>(null);
  useEffect(() => {
    const query = window.location.hash.split('?')[1];
    if (!query) return;
    const params = new URLSearchParams(query);
    const connected = params.get('connected') ?? undefined;
    const err = params.get('error') ?? undefined;
    if (!connected && !err) return;
    setOutcome({
      connected,
      error: err,
      detail: params.get('detail') ?? undefined,
      readScopeWithheld: params.get('read_scope_withheld') ?? undefined,
    });
    window.history.replaceState(null, '', `${window.location.pathname}#/sources`);
    void load();
  }, [load]);

  /* ⚠️ The provider's DISPLAY name, not the id from the callback URL. The callback can only carry the
     id, so these callouts read "hubspot is connected" one line above a card headed "HubSpot" — the
     kind of seam that reads as a different system rather than as the same one. Falls back to the id
     when the provider list has not arrived (or does not contain it), because a name we cannot resolve
     is better shown as the id than omitted. */
  const connectedName =
    providers?.find((p) => p.providerId === outcome?.connected)?.providerName ?? outcome?.connected;

  return (
    <div className="web-connect">
      {/* ⚠️ No `PageHeader` here. `AppShell` already renders the route's heading, so adding one gave
          the page **two `<h1>`s both reading "Sources"** — a duplicated heading is a visual defect and
          an accessibility one (a screen reader announces the page title twice and the outline has two
          roots). Found by reading the heading outline in a browser; no test asserted h1 uniqueness.
          The description lives as intro copy instead, which is what it always was. */}
      <p className="web-connect__intro">
        Where your records come from. A file works immediately; a live connection needs an app in that
        system first.
      </p>

      {/* ⚠️ Tied together deliberately, not two separate facts: this app is something you INSTALL —
          onto a laptop, a desktop, wherever it will run — and then point at your own systems with
          your own credentials. That is the whole operating model, and until this line existed nothing
          on the one screen where someone is about to add a real connection ever said so. The install
          mechanics (`InstallOffer`, `pwa.ts`) already existed but were reachable only from the CSV
          screen, disconnected from the moment they matter here. */}
      <InstallOffer title="Install this once, then connect as many systems as you like">
        Installed, it opens like any other app on this device — no browser tab to find again — and
        every connection you add still runs read-only until you approve a specific write.
      </InstallOffer>

      {/* ⚠️ A partial read grant is announced as **connected but narrower**, never as success and
          never as failure. It genuinely connected and can genuinely be pulled from; what changed is
          which objects will have data. Saying only "is connected" is what sent people into a 403
          days later with no idea a box had been unticked — and saying "did not complete" would be
          false, and would invite a reconnect that was not needed. */}
      {outcome?.connected && outcome.readScopeWithheld ? (
        <Callout tone="warning" title={`${connectedName} is connected, with less access than asked for`}>
          The consent screen did not grant <code>{outcome.readScopeWithheld}</code>. Anything needing
          those permissions will have no data until you reconnect and approve them. Everything else
          works.
        </Callout>
      ) : outcome?.connected ? (
        <Callout tone="success" title={`${connectedName} is connected`}>
          You can now pull records from it. Nothing has been pulled yet.
        </Callout>
      ) : null}
      {outcome?.error ? (
        <Callout tone="danger" title="That connection did not complete">
          {/* The provider's own code, verbatim. `access_denied` and `redirect_uri_mismatch` need
              completely different responses, and flattening them to "failed" hides which. */}
          <code>{outcome.error}</code>
          {outcome.detail ? ` — ${outcome.detail}` : null}
        </Callout>
      ) : null}

      {error ? (
        <Callout tone="warning" title="Connector list unavailable">
          {error}
        </Callout>
      ) : null}

      {providers === null && !error ? (
        <Skeleton variant="rect" height="7.5rem" />
      ) : (providers ?? []).length === 0 && !error ? (
        <EmptyState title="No connectors" description="No systems are configured for this deployment." />
      ) : (
        <div className="web-connect__grid">
          {(providers ?? []).map((provider) => (
            <ProviderCard
              key={provider.providerId}
              provider={provider}
              connections={connections}
              onConnect={startConnect}
            />
          ))}
        </div>
      )}

      <HelpText>
        Read-only access is all any connection asks for. Permission to write is requested later, per
        action, the first time you approve one — so connecting can never change anything in your system.
      </HelpText>
    </div>
  );
}
